


import { utils } from 'apprise-core/utils'

// something that can be done, a capability.
// once it is associated with a user, it models the right to trigger the action, ie. it becomes a permission.

export type Action = {   


    labels: string[]
    resource?: string
    type: string

}

// the static definition of an action, includes properties common to all instances.
// it's intended for authz checks: captures contextual expectations to match against instances.
// shouldn't be saved or exchanged on the wire.

export type ActionTemplate = Action & {

    //  "scoped" only to admins, cannot be carried out by tenant users.
    // (used by authz oracles, to limit implicit permissions of tenants manager.)
    actionType?: 'admin' | 'tenant'
}


export const any = "*"


export const do_anything: ActionTemplate = {
    type: any,
    labels: [any],
    resource: any,
    actionType: 'admin'
   
}

export const do_nothing: ActionTemplate = {
    type: any,
    labels: [],
    resource: any
}

export const manageRights: ActionTemplate = { actionType: 'admin', type: 'rights', labels: ["manage", "rights"] }


// utility functions and a comparison algebra to use as the basis for an authorization api.
export const   getActions = () => {


    const self = {

        concat: (type: string, labels: string[], resource: string = any) => `${type}:${labels.join(":")}:${resource}`

        ,

        idOf: ({ type, labels, resource }: Action) => self.concat(type, labels, resource)

        ,

        
        // restricts a template over a specific resource (but still returns it).
        // use to narrow an authz check
        specialise: <A extends ActionTemplate> (a: A, resource?: string): A => ({ ...a, resource })

        ,

        // creates an instance from a template, optionally with a specific resource.
        // use to store actions in models.
        instantiate: (template: ActionTemplate, resource?: string): Action => ({ type: template.type, labels: template.labels, resource: resource ?? template.resource ?? any })

        ,

        // the first has at least the same labels as the second, where 'same' includes also an any match.
        matchLabels: (a1: Action, a2: Action) => !!a2.labels.length && !a2.labels.some((lbl, i) => lbl !== any && lbl !== a1.labels[i])

        ,

        /** match labels as well as type. */
        match: (a1: Action, a2: Action) => {

            const matchtype = (a1.type === a2.type || a2.type === any || !a2.type)

            // matching type: same (including both any or missing), or action's is any or missing
            return !!matchtype && self.matchLabels(a1, a2)

        }

        ,

        /** at least one matches labels as well as type. */
        matchAny: (a: Action, actions: Action[]) => actions.some(aa => self.match(a, aa))

        ,


        /** at least one matches labels as well as type. */
        matchAnyOther: (a: Action, actions: Action[]) => actions.some(aa => self.match(a, aa) && !self.is(a, aa))

        ,

        /** matches labels, types, and resource */
        impliedBy: (a1: Action, a2: Action) => self.match(a1, a2) && (a1.resource === a2.resource || a2.resource === any || a2.resource === undefined)

        ,

        /** at least one matches labels, types, and resource */
        impliedByAny: (a: Action, actions: Action[]) => actions.some(aa => self.impliedBy(a, aa))

        ,

        /** same labels and same type. */
        isLike: (a1: Action, a2: Action) => self.match(a1, a2) && self.match(a2, a1)

        ,

        /** at least one has same labels and same type. */
        isLikeAny: (a: Action, actions: Action[]) => actions.some(aa => self.isLike(aa, a))

        ,

        /** at least one has same labels, same type, and same resource (deeply equals once externed). */
        is: (a1: Action, a2: Action) => utils().deepequals({ type: a1.type, labels: a1.labels, resource: a1.resource || any }, { type: a2.type, labels: a2.labels, resource: a2.resource || any })

        ,

        /** at least one has same labels, same type, and same resource. */
        isAny: (a: Action, actions: Action[]) => actions.some(aa => self.is(aa, a))

        ,

        stringify: (a: Action) => `${self.idOf(a)}:${a.resource}`

        ,

        // order by umplication, then lexicographically
        comparator: (a1: Action, a2: Action) => self.impliedBy(a1, a2) ? self.impliedBy(a2, a1) ? 0 : 1 : self.impliedBy(a2, a1) ? -1 : self.stringify(a2).localeCompare(self.stringify(a1))

        ,

        reconcile: <A extends Action>(actions: A[]): A[] => {

            // sort upfront from most generic to most specific, so that reconciliation can be treated in 'streaming' fashion
            const sorted = [...actions]

            sorted.sort(self.comparator)

            //  simply add one by one if not already implied by previous ones
            return sorted.reduce((acc, a) => self.impliedByAny(a, acc) ? acc : [a, ...acc], [] as A[])

        }

    }

    return self
}
